import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { WebUxTitle } from '../WebUxContext';
import { Button, DatePicker, Select, Table } from 'antd';
import { useBusinessContext } from '../../context/BusinessContext';
import { DayJs } from '../../common/Utils';
import GeneralLedgerList, { GeneralLedgerListType } from './GeneralLedgerList';

type Props = {};

export const GeneralLedgerPage: FC<Props> = (props) => {
  const business = useBusinessContext();

  const listRef = useRef<GeneralLedgerListType>(null);
  const [range, setRange] = useState<string>('today');
  const [from, setFrom] = useState<DayJs.Dayjs>(DayJs());
  const [to, setTo] = useState<DayJs.Dayjs>(DayJs());
  const [account, setAccount] = useState<string>('0');
  const [site, setSite] = useState<string>('0');

  const dateRanges = useMemo(() => {
    const financialYears = [...new Array(5).keys()].map((y) =>
      DayJs().subtract(y, 'year'),
    );
    const ranges: any = [
      { label: 'Custom', value: 'custom' },
      {
        label: 'Recent',
        options: [
          { label: 'Today', value: 'today' },
          { label: 'Yesterday', value: 'yesterday' },
          { label: 'This Week', value: 'this-week' },
          { label: 'Last Week', value: 'last-week' },
          { label: 'This Month', value: 'this-month' },
          { label: 'Last Month', value: 'last-month' },
        ],
      },
      {
        label: 'Financial Year',
        options: financialYears.map((y) => ({
          label: `${y.year()} - ${y.year() + 1}`,
          value: `financial:${y.year()}`,
        })),
      },
    ];

    return ranges;
  }, []);

  const accountOptions = useMemo(() => {
    return Object.values(business.accountTypes)
      .map((at) => ({
        label: at.name,
        options: Object.values(business.accounts)
          .filter((acc) => acc.accountTypeId === at.accountTypeId)
          .map((acc) => ({
            label: acc.name,
            value: acc.accountId,
          })),
      }))
      .filter((at) => at.options.length > 0);
  }, [business.accountTypes, business.accounts]);

  const siteOptions = useMemo(() => {
    return [
      { label: 'All Sites', value: '0' },
      {
        label: 'Sites',
        options: Object.values(business.sites).map((s) => ({
          label: s.name,
          value: s.siteId,
        })),
      },
    ];
  }, [business.sites]);

  const updateRange = useCallback((r: string) => {
    setRange(r);
    if (r === 'today') {
      setFrom(DayJs());
      setTo(DayJs());
    } else if (r === 'yesterday') {
      setFrom(DayJs().subtract(1, 'd'));
      setTo(DayJs().subtract(1, 'd'));
    } else if (r === 'this-week') {
      setFrom(DayJs().startOf('w'));
      setTo(DayJs().endOf('w'));
    } else if (r === 'last-week') {
      setFrom(DayJs().subtract(1, 'w').startOf('w'));
      setTo(DayJs().subtract(1, 'w').endOf('w'));
    } else if (r === 'this-month') {
      setFrom(DayJs().startOf('month'));
      setTo(DayJs().endOf('month'));
    } else if (r === 'last-month') {
      setFrom(DayJs().subtract(1, 'month').startOf('month'));
      setTo(DayJs().subtract(1, 'month').endOf('month'));
    } else if (r.startsWith('financial:')) {
      const start = `${r.split(':')[1]}-04-01`;
      console.log(start);
      setFrom(DayJs(start, 'YYYY-MM-DD'));
      setTo(DayJs(start, 'YYYY-MM-DD').add(1, 'year').subtract(1, 'd'));
    }
  }, []);

  return (
    <div className="flex flex-col gap-2">
      <WebUxTitle>General Ledger</WebUxTitle>
      <div className="flex flex-col gap-2">
        <div className="flex justify-between gap-1">
          <div className="flex gap-4">
            <div className="flex flex-col gap-1">
              <label>Site</label>
              <Select
                className="w-[150px]"
                options={siteOptions}
                value={site}
                onChange={setSite}
              />
            </div>
            <div className="flex flex-col gap-1">
              <label>Account</label>
              <Select
                showSearch={true}
                optionFilterProp="label"
                className="w-[250px]"
                options={accountOptions}
                value={account}
                onChange={setAccount}
              />
            </div>

            <div className="flex flex-col gap-1">
              <label>Date Range</label>
              <div className="flex gap-2">
                <Select
                  className="w-[200px]"
                  options={dateRanges}
                  value={range}
                  onChange={updateRange}
                />
                <DatePicker
                  value={from}
                  allowClear={false}
                  onChange={(date) => {
                    setFrom(date);
                    setRange('custom');
                  }}
                  disabledDate={(date) => date.isAfter(to)}
                />
                <DatePicker
                  value={to}
                  allowClear={false}
                  onChange={(date) => {
                    setTo(date);
                    setRange('custom');
                  }}
                  disabledDate={(date) => date.isBefore(from)}
                />
                <Button
                  type="primary"
                  onClick={() =>
                    listRef.current?.update(account, site, from, to)
                  }
                >
                  Update Report
                </Button>
              </div>
            </div>
          </div>
        </div>
        <GeneralLedgerList ref={listRef} />
      </div>
    </div>
  );
};
